import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Wallet, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Payroll() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const load = () => api.get("/payroll/runs").then((r) => setRuns(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try { await api.post(`/payroll/runs/${id}/approve`); toast.success("Approval recorded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Payroll · HITL two-key approval</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Payroll Runs</h1>
      </div>
      <div className="grid md:grid-cols-3 gap-0 border border-border rounded-lg overflow-hidden bg-white">
        {[
          { icon: Wallet, label: "Runs YTD", v: runs.length },
          { icon: CheckCircle2, label: "Approved", v: runs.filter((r) => r.status === "approved").length },
          { icon: Clock, label: "Pending", v: runs.filter((r) => r.status === "pending_approval").length },
        ].map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className={`p-6 ${i < 2 ? "md:border-r border-border" : ""}`}>
              <Icon className="h-5 w-5 text-[#0047AB] mb-3" strokeWidth={1.5} />
              <div className="font-display text-3xl font-bold tracking-tight">{k.v}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{k.label}</div>
            </div>
          );
        })}
      </div>
      <Card className="border-border shadow-none overflow-hidden">
        <div className="p-5 border-b border-border flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <span className="text-xs uppercase tracking-widest font-bold">Two-key approval required</span>
          <span className="text-xs text-muted-foreground ml-2">— every run needs two admin signatures.</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Headcount</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Approvals</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((r) => (
              <TableRow key={r.id} data-testid={`payroll-row-${r.id}`}>
                <TableCell className="font-mono font-semibold">{r.period}</TableCell>
                <TableCell>{r.headcount}</TableCell>
                <TableCell className="text-right font-mono">${r.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                <TableCell><span className="font-mono text-xs">{r.approvals.length}/2</span></TableCell>
                <TableCell>
                  {r.status === "approved" ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">Approved</Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50">Pending</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "pending_approval" && user?.role === "admin" && !r.approvals.some((a) => a.by === user.email) ? (
                    <Button size="sm" data-testid={`payroll-approve-${r.id}`} onClick={() => approve(r.id)} className="bg-[#0047AB] hover:bg-[#003380]">Sign approval</Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
